import * as Sentry from "@sentry/react";
import type { SentryUserContext } from "@/application/ports/IMonitoring";
import { logger } from "@/infrastructure/logger";
import { AppError } from "@/domain/errors/AppError";

/**
 * Frontend transaction routes that get 100% trace sampling.
 *
 * Mirrors the backend's critical-paths list (see `_API_CRITICAL_PATHS` in
 * `services/api/app/infrastructure/monitoring/sentry_client.py`) so a user
 * action that pages someone on the backend is guaranteed to have a matching
 * frontend trace. Anything not matching falls back to `DEFAULT_TRACES_RATE`.
 */
const CRITICAL_TRACE_PATTERNS: RegExp[] = [
  /^\/auth(\/|$)/,
  /^\/hub\/signing-callback/,
  /^\/hub\/investments\/[0-9a-f-]{36}\/action/i,
  /^\/investments\/contracts/,
  /^\/investments\/[0-9a-f-]{36}$/i,
];

const DEFAULT_TRACES_RATE = 0.1;

type SamplingContext = Parameters<NonNullable<Sentry.BrowserOptions["tracesSampler"]>>[0];

export function sampleTransaction(samplingContext: SamplingContext): number | boolean {
  if (samplingContext.parentSampled !== undefined) {
    return samplingContext.parentSampled;
  }

  const name = samplingContext.name ?? "";
  const pathname =
    typeof samplingContext.attributes?.["url.path"] === "string"
      ? (samplingContext.attributes["url.path"] as string)
      : name;

  if (CRITICAL_TRACE_PATTERNS.some((pattern) => pattern.test(pathname))) {
    return 1.0;
  }

  return DEFAULT_TRACES_RATE;
}

/**
 * Initialize Sentry monitoring for error tracking and performance monitoring.
 * Only initializes in production environment with valid DSN.
 */
export function initMonitoring(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";
  const appVersion = import.meta.env.VITE_APP_VERSION || "0.0.0";

  // Only initialize in non-development environments with valid DSN
  if (!dsn || environment === "development") {
    logger.info(
      `[Monitoring] Sentry disabled (environment: ${environment}, dsn: ${dsn ? "present" : "missing"})`,
    );
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      release: `hulahoop-frontend@${appVersion}`,

      // Performance Monitoring — sampler matches backend critical paths
      // so cross-project filters / dashboards stay coherent.
      tracesSampler: sampleTransaction,
      tracePropagationTargets: [
        "localhost",
        /^https:\/\/api\.hulahoop\.pro/,
        /^https:\/\/hulahoop-pro-backend-[a-z0-9-]+-[a-z0-9]+\.a\.run\.app/,
      ],

      // Session Replay (for debugging user sessions)
      replaysSessionSampleRate: 0.1, // 10% of normal sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      integrations: [
        Sentry.browserTracingIntegration({
          // Track React Router navigation
          enableInp: true,
        }),
        Sentry.replayIntegration({
          maskAllText: true, // Mask all text for privacy
          blockAllMedia: true, // Block images/videos
          maskAllInputs: true, // Mask input fields
        }),
        Sentry.browserProfilingIntegration(),
      ],

      // Filter sensitive data and operational errors
      beforeSend(event, hint) {
        // Drop operational errors (404s, validation, business rules) — they are expected
        const exception = hint?.originalException;
        if (exception instanceof AppError && exception.isOperational) {
          return null;
        }

        // Remove passwords and tokens from request data
        if (event.request?.data) {
          const data = event.request.data as Record<string, unknown>;
          if (data.password) data.password = "[Filtered]";
          if (data.token) data.token = "[Filtered]";
          if (data.access_token) data.access_token = "[Filtered]";
          if (data.refresh_token) data.refresh_token = "[Filtered]";
        }

        // Filter Authorization headers
        if (event.request?.headers) {
          if (event.request.headers.Authorization) {
            event.request.headers.Authorization = "[Filtered]";
          }
        }

        return event;
      },

      // Ignore common non-critical errors
      ignoreErrors: [
        // Browser extensions
        "top.GLOBALS",
        "canvas.contentDocument",
        // Network errors (already logged)
        "Network request failed",
        "Failed to fetch",
        "NetworkError",
        // React errors that are handled
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        // Non-Error promise rejections
        "Non-Error promise rejection captured",
        // Aborted requests (user navigation)
        "AbortError",
        "The operation was aborted",
      ],

      // Don't report errors from browser extensions
      denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
    });

    logger.info(
      `[Monitoring] Sentry initialized (environment: ${environment}, release: hulahoop-frontend@${appVersion})`,
    );
  } catch (error) {
    logger.error("[Monitoring] Failed to initialize Sentry:", error);
  }
}

/**
 * Set the canonical user + organization context on the current Sentry scope.
 * Shape must match the backend's `apply_user_context` so filters work
 * cross-project — see `docs/standards/infra/observability/strategy-and-sentry.md` §2.10.
 *
 * Tags `organization_uuid` and `role` mirror the user-payload fields so
 * Sentry's tag-based search ("all errors of org X") works without custom
 * syntax against the user object.
 */
export function setUserContext(user: SentryUserContext): void {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username ?? undefined,
    organization_uuid: user.organization_uuid,
    organization_name: user.organization_name,
    role: user.role,
  });

  if (user.organization_uuid) {
    Sentry.setTag("organization_uuid", user.organization_uuid);
  }
  if (user.role) {
    Sentry.setTag("role", user.role);
  }
}

/**
 * Clear user context + filter tags (on logout / unauthenticated state).
 *
 * Sentry's public API has no "remove tag". Setting the value to `undefined`
 * keeps the key in the in-memory scope but JSON.stringify drops it during
 * event serialization, so subsequent events are not attributed to the
 * previous organization.
 */
export function clearUserContext(): void {
  Sentry.setUser(null);
  Sentry.setTag("organization_uuid", undefined);
  Sentry.setTag("role", undefined);
}

/**
 * Add a breadcrumb for tracking user actions and API calls.
 * Breadcrumbs are shown in the timeline leading up to an error.
 */
export function addBreadcrumb(breadcrumb: {
  category: string;
  message: string;
  level?: "debug" | "info" | "warning" | "error";
  data?: Record<string, unknown>;
}): void {
  Sentry.addBreadcrumb({
    category: breadcrumb.category,
    message: breadcrumb.message,
    level: breadcrumb.level || "info",
    data: breadcrumb.data,
  });
}

/**
 * Manually capture an exception with optional fingerprinting and tags.
 * Fingerprinting controls how errors are grouped in Sentry.
 */
export function captureException(
  error: Error,
  context?: {
    fingerprint?: string[];
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  },
): string | undefined {
  return Sentry.withScope((scope) => {
    if (context?.fingerprint) {
      scope.setFingerprint(context.fingerprint);
    }
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    if (context?.extra) {
      scope.setExtras(context.extra);
    }
    return Sentry.captureException(error);
  });
}

/**
 * Manually capture a message
 */
export function captureMessage(
  message: string,
  level: "fatal" | "error" | "warning" | "info" | "debug" = "info",
): string | undefined {
  return Sentry.captureMessage(message, level);
}
